import { useLocation, useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { useEffect, useState } from "react";
import { Box, Button, Tab, Tabs } from "@mui/material";
import SearchBar from "../../components/SearchBar";
import DownloadIcon from "@mui/icons-material/Download";
import React from "react";
import FilterMenu from "../../components/filterMenu";
import { formatAEDNumber, isValidInput } from "../../utils/validations";
import {
  useExportBookingPaymentMutation,
  useExportCancelTransactionMutation,
  useExportDirectLinkPaymentMutation,
  useExportSubscriptionMutation,
  useLazyCancelTransactionDetailsQuery,
  useLazyGetBookingPaymentsQuery,
  useLazyGetDirectLinkPaymentsQuery,
  useLazyGetTransactionsQuery,
} from "../../service/subscription";
import { showError } from "../../constants/toasts";
import {
  BookingTransactions,
  Categories,
  PaymentDetail,
} from "../../types/General";
import Loader from "../../constants/Loader";
import CustomPagination from "../../components/CustomPagination";
import { formatDubaiDateTime } from "../../utils/dateTime";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import dayjs from "dayjs";
import {
  resetSubscriptionPaymentFilter,
  setSubscriptionPaymentFilter,
} from "../../reducers/subscriptionPaymentsFilterSlice";
import TransactionFilters from "../../components/TransactionFilters";
import { useLazyGetCategoryQuery } from "../../service/category";
import BookingPaymentFilters from "../../components/BookingPaymentFilters";
import {
  resetBookingPaymentFilter,
  setBookingPaymentFilter,
} from "../../reducers/bookingPaymentsFilterSlice";
import CancelPaymentFilter from "../../components/CancelPaymentFilter";
import {
  resetCancelledPaymentFilter,
  setCancelledPaymentFilter,
} from "../../reducers/cancelledPaymentFilterSlice";

enum TRANSACTION_TYPE {
  SUBSCRIPTION = 0,
  BOOKINGS = 1,
  CANCELLED_BOOKINGS = 2,
  DIRECT_LINK = 3,
}

function CustomTabPanel(props: {
  children?: React.ReactNode;
  index: number;
  value: number;
}) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageTransactions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const status = state?.status || "";

  const dispatch = useAppDispatch();
  const subscriptionPaymentsFilter = useAppSelector(
    (state: RootState) => state.subscriptionPaymentsFilter
  );
  const bookingPaymentsFilter = useAppSelector(
    (state: RootState) => state.bookingPaymentsFilter
  );
  const cancelTransactionFilter = useAppSelector(
    (state: RootState) => state.cancelledPaymentFilter
  );

  const [getTransactions] = useLazyGetTransactionsQuery();
  const [bookingPayments] = useLazyGetBookingPaymentsQuery();
  const [directLink] = useLazyGetDirectLinkPaymentsQuery();
  const [cancelTransactionDetails] = useLazyCancelTransactionDetailsQuery();
  const [exportBookingPayments] = useExportBookingPaymentMutation();
  const [exportSubscription] = useExportSubscriptionMutation();
  const [exportDirectLink] = useExportDirectLinkPaymentMutation();
  const [exportCancelTransaction] = useExportCancelTransactionMutation();
  const [getCategory] = useLazyGetCategoryQuery();

  const [searchTerm, setSearchTerm] = useState("");
  const [valueTabs, setValueTabs] = useState(0);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(10);
  const [transactions, setTransactions] = useState<PaymentDetail[]>([]);
  const [bookingTransactions, setBookingTransactions] = useState<
    BookingTransactions[]
  >([]);
  const [directLinkData, setDirectLinkData] = useState([]);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingCount, setBookingsCount] = useState(10);
  const [directLinkPage, setDirectLinkPage] = useState(1);
  const [directLinkCount, setDirectLinkCount] = useState(10);
  const [cancelTransactionPage, setCancelTransactionPage] = useState(1);
  const [cancelTransactionCount, setCancelTransactionCount] = useState(10);
  const [cancelTransactionData, setCancelTransactionData] = useState([]);
  const [totalAmount, setTotalAmount] = useState("");
  const [refundedAmount, setRefundedAmount] = useState("");
  const [categoryData, setCategoryData] = useState<Categories[]>([]);

  const getTranscationApi = async (clearAll?: any) => {
    setLoading(true);
    try {
      const body = {
        ...(subscriptionPaymentsFilter.startRange && !clearAll
          ? {
              startDate: dayjs(subscriptionPaymentsFilter.startRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(subscriptionPaymentsFilter.endRange && !clearAll
          ? {
              endDate: dayjs(subscriptionPaymentsFilter.endRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(subscriptionPaymentsFilter.membershipType && !clearAll
          ? { membershipType: subscriptionPaymentsFilter.membershipType }
          : {}),
        ...(subscriptionPaymentsFilter.paymentStatus && !clearAll
          ? { paymentStatus: subscriptionPaymentsFilter.paymentStatus }
          : {}),
      };

      const response = await getTransactions({
        search: debouncedSearchTerm.trim(),
        page,
        body,
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setTransactions(response?.data?.result?.items || []);
        setCount(response?.data?.result?.totalCount);
        setTotalAmount(response?.data?.subscriptionAmount || "");
        setRefundedAmount(response?.data?.refundedAmount || "");
      } else {
        setTransactions([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getBookingPaymentsApi = async (clearAll?: any) => {
    setLoading(true);
    try {
      const body = {
        ...(bookingPaymentsFilter.startRange && !clearAll
          ? {
              startDate: dayjs(bookingPaymentsFilter.startRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(bookingPaymentsFilter.endRange && !clearAll
          ? {
              endDate: dayjs(bookingPaymentsFilter.endRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(bookingPaymentsFilter.categoryId && !clearAll
          ? { categoryId: bookingPaymentsFilter.categoryId }
          : {}),
      };

      const response = await bookingPayments({
        search: debouncedSearchTerm.trim(),
        page: bookingPage,
        body,
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setBookingTransactions(response?.data?.result?.items || []);
        setBookingsCount(response?.data?.result?.totalCount);
        setTotalAmount(response?.data?.totalAmount || "");
      } else {
        setBookingTransactions([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getDirectLinkPaymentsApi = async () => {
    setLoading(true);
    try {
      const response = await directLink({
        search: debouncedSearchTerm.trim(),
        page: directLinkPage,
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setDirectLinkData(response?.data?.result?.items || []);
        setDirectLinkCount(response?.data?.result?.totalCount);
        setTotalAmount(response?.data?.totalPaidAmount || "");
      } else {
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getCancelTransactionDetailsApi = async (clearAll?: any) => {
    setLoading(true);
    try {
      const body = {
        ...(cancelTransactionFilter.startRange && !clearAll
          ? {
              startDate: dayjs(cancelTransactionFilter.startRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(cancelTransactionFilter.endRange && !clearAll
          ? {
              endDate: dayjs(cancelTransactionFilter.endRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(cancelTransactionFilter.categoryId && !clearAll
          ? { categoryId: cancelTransactionFilter.categoryId }
          : {}),
      };

      const response = await cancelTransactionDetails({
        search: debouncedSearchTerm.trim(),
        page: cancelTransactionPage,
        body,
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.status === 200) {
        setCancelTransactionData(response?.data?.result?.items || []);
        setCancelTransactionCount(response?.data?.result?.totalCount);
        setTotalAmount(response?.data?.totalAmount || "");
      } else {
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const exportBookingPaymentsApi = async () => {
    setLoading(true);
    try {
      const response: any = await exportBookingPayments({
        format: "excel",
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.statusCode === 200) {
        window.open(response?.downloadUrl, "_blank");
      } else {
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const exportSubscriptionApi = async () => {
    setLoading(true);
    try {
      const response: any = await exportSubscription({
        format: "excel",
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.statusCode === 200) {
        window.open(response?.downloadUrl, "_blank");
      } else {
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const exportDirectLinkApi = async () => {
    setLoading(true);
    try {
      const response: any = await exportDirectLink({
        format: "excel",
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.statusCode === 200) {
        window.open(response?.downloadUrl, "_blank");
      } else {
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const exportCancelTransactionApi = async () => {
    setLoading(true);
    try {
      const response: any = await exportCancelTransaction({
        format: "excel",
      }).unwrap();
      setLoading(false);
      setHasFetched(true);
      if (response.statusCode === 200) {
        window.open(response?.downloadUrl, "_blank");
      } else {
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getCategoryApi = async () => {
    try {
      const response = await getCategory({ search: "" }).unwrap();
      if (response.status === 200) {
        setCategoryData(response?.data || []);
        // setCount(response?.data?.totalCount);
      } else {
        setCategoryData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleFilterChangeSubscriptionPayments = (key: string, value: any) => {
    dispatch(setSubscriptionPaymentFilter({ [key]: value }));
  };

  const handleClearAllSubscriptionPayments = () => {
    dispatch(resetSubscriptionPaymentFilter());
  };

  const handleFilterChangeBookingPayments = (key: string, value: any) => {
    dispatch(setBookingPaymentFilter({ [key]: value }));
  };

  const handleClearAllBookingPayments = () => {
    dispatch(resetBookingPaymentFilter());
  };

  const handleFilterChangeCancelTransaction = (key: string, value: any) => {
    dispatch(setCancelledPaymentFilter({ [key]: value }));
  };

  const handleClearAllCancelTransaction = () => {
    dispatch(resetCancelledPaymentFilter());
  };

  useEffect(() => {
    if (valueTabs === TRANSACTION_TYPE.SUBSCRIPTION) {
      getTranscationApi();
    }
  }, [page, debouncedSearchTerm, valueTabs]);

  useEffect(() => {
    if (valueTabs === TRANSACTION_TYPE.BOOKINGS) {
      getBookingPaymentsApi();
      getCategoryApi();
    }
  }, [bookingPage, valueTabs, debouncedSearchTerm]);

  useEffect(() => {
    if (valueTabs === TRANSACTION_TYPE.DIRECT_LINK) {
      getDirectLinkPaymentsApi();
    }
  }, [directLinkPage, valueTabs, debouncedSearchTerm]);

  useEffect(() => {
    if (valueTabs === TRANSACTION_TYPE.CANCELLED_BOOKINGS) {
      getCancelTransactionDetailsApi();
      getCategoryApi();
    }
  }, [cancelTransactionPage, valueTabs, debouncedSearchTerm]);

  useEffect(() => {
    if (status) {
      setValueTabs(status);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Manage Transactions</h1>
      </div>

      <Box className="custom_tabs2 mt_40 mb_30" sx={{ width: "100%" }}>
        <Tabs
          value={valueTabs}
          onChange={(e, val) => {
            setPage(1);
            setValueTabs(val);
          }}
          className="custom_tabs_links"
        >
          <Tab label="Subscriptions" {...a11yProps(0)} />
          <Tab label="Bookings" {...a11yProps(1)} />
          <Tab label="Cancelled Bookings" {...a11yProps(2)} />
          <Tab label="Direct Link" {...a11yProps(3)} />
        </Tabs>
      </Box>
      <div className="stats_boxes gap_m">
        <div className="cards">
          <h2>Total Amount</h2>
          <h3>AED {totalAmount ? formatAEDNumber(totalAmount) : "0"}</h3>
        </div>
        {valueTabs === TRANSACTION_TYPE.SUBSCRIPTION && (
          <div className="cards">
            <h2>Refunded Amount</h2>
            <h3>
              AED {refundedAmount ? formatAEDNumber(refundedAmount) : "0"}
            </h3>
          </div>
        )}
      </div>
      <div className="cards mt_20">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                searchTerm={searchTerm}
                placeholder="Search...."
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          <div className="right_s">
            {valueTabs === TRANSACTION_TYPE.SUBSCRIPTION ? (
              <TransactionFilters
                startDate={subscriptionPaymentsFilter.startRange}
                endDate={subscriptionPaymentsFilter.endRange}
                setStartDate={(date) =>
                  handleFilterChangeSubscriptionPayments("startRange", date)
                }
                setEndDate={(date) =>
                  handleFilterChangeSubscriptionPayments("endRange", date)
                }
                handleSubmit={getTranscationApi}
                handleClearFilters={handleClearAllSubscriptionPayments}
                membershipType={subscriptionPaymentsFilter.membershipType}
                setMembershipType={(value) =>
                  handleFilterChangeSubscriptionPayments(
                    "membershipType",
                    value
                  )
                }
                paymentStatus={subscriptionPaymentsFilter.paymentStatus}
                setPaymentStatus={(value) =>
                  handleFilterChangeSubscriptionPayments("paymentStatus", value)
                }
              />
            ) : valueTabs === TRANSACTION_TYPE.BOOKINGS ? (
              <BookingPaymentFilters
                startDate={bookingPaymentsFilter.startRange}
                endDate={bookingPaymentsFilter.endRange}
                setStartDate={(date) =>
                  handleFilterChangeBookingPayments("startRange", date)
                }
                setEndDate={(date) =>
                  handleFilterChangeBookingPayments("endRange", date)
                }
                handleSubmit={getBookingPaymentsApi}
                handleClearFilters={handleClearAllBookingPayments}
                category={categoryData || []}
                categoryId={bookingPaymentsFilter.categoryId}
                setCategoryId={(value) =>
                  handleFilterChangeBookingPayments("categoryId", value)
                }
              />
            ) : valueTabs === TRANSACTION_TYPE.CANCELLED_BOOKINGS ? (
              <CancelPaymentFilter
                startDate={cancelTransactionFilter.startRange}
                endDate={cancelTransactionFilter.endRange}
                setStartDate={(date) =>
                  handleFilterChangeCancelTransaction("startRange", date)
                }
                setEndDate={(date) =>
                  handleFilterChangeCancelTransaction("endRange", date)
                }
                handleSubmit={getCancelTransactionDetailsApi}
                handleClearFilters={handleClearAllCancelTransaction}
                category={categoryData || []}
                categoryId={cancelTransactionFilter.categoryId}
                setCategoryId={(value) =>
                  handleFilterChangeCancelTransaction("categoryId", value)
                }
              />
            ) : null}

            <Button
              className="btn btn_grey"
              onClick={() => {
                if (valueTabs === TRANSACTION_TYPE.SUBSCRIPTION) {
                  exportSubscriptionApi();
                } else if (valueTabs === TRANSACTION_TYPE.BOOKINGS) {
                  exportBookingPaymentsApi();
                } else if (valueTabs === TRANSACTION_TYPE.DIRECT_LINK) {
                  exportDirectLinkApi();
                } else if (valueTabs === TRANSACTION_TYPE.CANCELLED_BOOKINGS) {
                  exportCancelTransactionApi();
                }
              }}
            >
              <DownloadIcon /> Export
            </Button>
          </div>
        </div>

        {/* User Transactions Table */}
        <CustomTabPanel value={valueTabs} index={0}>
          <div className="table_container">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>User Name</th>
                  <th>Email</th>
                  <th>Membership Type</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions?.length ? (
                  transactions?.map((item) => (
                    <tr key={item?.paymentId}>
                      <td>{`${item?.transactionId ? item?.transactionId : "-"}`}</td>
                      <td
                        className="cursor_p"
                        onClick={() =>
                          navigate(
                            `/manage-customers/detail/${item?.payerUserId}`,
                            {
                              state: {
                                status: "APPROVED",
                                memberType: item?.membership,
                                transaction: true,
                                subTab: valueTabs,
                              },
                            }
                          )
                        }
                      >
                        {item?.payerName || "-"}
                      </td>
                      <td>{item?.payerEmail || "-"}</td>
                      <td>{item?.membership || "-"}</td>
                      <td>{item?.status || "-"}</td>
                      <td>
                        {item?.amount
                          ? `AED ${formatAEDNumber(item?.amount)}`
                          : "-"}
                      </td>
                      <td>
                        {item?.createdAt
                          ? formatDubaiDateTime(item?.createdAt)
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : hasFetched ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No Data Found
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {transactions?.length ? (
            <CustomPagination
              currentPage={page - 1}
              totalPages={Math.ceil(count / 10)}
              count={count}
              pageSize={10}
              onPageChange={(page) => setPage(page + 1)}
            />
          ) : null}
        </CustomTabPanel>

        {/* Offers Table */}
        <CustomTabPanel value={valueTabs} index={1}>
          <div className="table_container">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Booking ID</th>
                  <th>Offer Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Category</th>
                  <th>Transaction Date</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {bookingTransactions?.length ? (
                  bookingTransactions?.map((item) => (
                    <tr key={item?.id}>
                      <td>{item?.bookingTransactionId || "-"}</td>
                      <td
                        className="cursor_p"
                        onClick={() =>
                          navigate(
                            `/manage-booking-resvp/booking-detail/${item?.guidBookingId}`,
                            {
                              state: {
                                transaction: true,
                                status: valueTabs,
                              },
                            }
                          )
                        }
                      >
                        {item?.bookingId || "-"}
                      </td>

                      <td>{item?.offerName || "-"}</td>
                      <td>
                        {item?.offerStartDate
                          ? formatDubaiDateTime(item?.offerStartDate)
                          : "-"}
                      </td>
                      <td>
                        {item?.offerEndDate
                          ? formatDubaiDateTime(item?.offerEndDate)
                          : "-"}
                      </td>
                      <td>{item?.categoryName || "-"}</td>
                      <td>
                        {item?.createAt
                          ? formatDubaiDateTime(item?.createAt)
                          : ""}
                      </td>

                      <td>AED {formatAEDNumber(item?.amountPaid) || "0"}</td>
                    </tr>
                  ))
                ) : hasFetched ? (
                  <tr>
                    <td
                      colSpan={10}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No Data Found
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {bookingTransactions?.length ? (
            <CustomPagination
              currentPage={bookingPage - 1}
              totalPages={Math.ceil(bookingCount / 10)}
              count={bookingCount}
              pageSize={10}
              onPageChange={(page) => setBookingPage(page + 1)}
            />
          ) : null}
        </CustomTabPanel>

        {/* Cancellations Table */}
        <CustomTabPanel value={valueTabs} index={2}>
          <div className="table_container">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Booking ID</th>
                  <th>Offer Name</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Employee Name</th>
                  <th>Client Name</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {cancelTransactionData?.length ? (
                  cancelTransactionData?.map((item: any) => (
                    <tr key={item?.id}>
                      <td>{item?.cancelBookingTransactionId}</td>
                      <td
                        className="cursor_p"
                        onClick={() =>
                          navigate(
                            `/manage-booking-resvp/booking-detail/${item?.guidBookingId}`,
                            {
                              state: {
                                transaction: true,
                                status: valueTabs,
                              },
                            }
                          )
                        }
                      >
                        {item?.bookingId || "-"}
                      </td>
                      <td>{item?.offerName || "-"}</td>
                      <td>{item?.categoryName || "-"}</td>
                      <td>AED {item?.amount || "0"}</td>
                      <td>{item?.employeeName || "-"}</td>
                      <td>{item?.clientName || "-"}</td>
                      <td>
                        {item?.createAt
                          ? formatDubaiDateTime(item?.createAt)
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {cancelTransactionData?.length ? (
            <CustomPagination
              currentPage={cancelTransactionPage - 1}
              totalPages={Math.ceil(cancelTransactionCount / 10)}
              count={cancelTransactionCount}
              pageSize={10}
              onPageChange={(page) => setCancelTransactionPage(page + 1)}
            />
          ) : null}
        </CustomTabPanel>

        <CustomTabPanel value={valueTabs} index={3}>
          <div className="table_container">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Amount</th>
                  <th>Member Name</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {directLinkData?.length ? (
                  directLinkData?.map((item: any) => (
                    <tr key={item?.paymentId}>
                      <td>{item?.transactionId || "-"}</td>
                      <td>AED {formatAEDNumber(item?.amount) || "0"}</td>
                      <td
                        className="cursor_p"
                        onClick={() =>
                          navigate(
                            `/manage-customers/detail/${item?.user?.id}`,
                            {
                              state: {
                                status: "APPROVED",
                                memberType: item?.user?.membershipType,
                                transaction: true,
                                subTab: valueTabs,
                              },
                            }
                          )
                        }
                      >
                        {item?.user?.name || "-"}
                      </td>
                      <td>{item?.status || "-"}</td>
                      <td>
                        {item?.paymentDate
                          ? formatDubaiDateTime(item?.paymentDate)
                          : "-"}
                      </td>
                      <td>{item?.notes || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {directLinkData?.length ? (
            <CustomPagination
              currentPage={directLinkPage - 1}
              totalPages={Math.ceil(directLinkCount / 10)}
              count={directLinkCount}
              pageSize={10}
              onPageChange={(page) => setDirectLinkPage(page + 1)}
            />
          ) : null}
        </CustomTabPanel>
      </div>
    </MainContainer>
  );
};

export default ManageTransactions;
